import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { ACTIVE_CLASS_NAME } from 'modules/nav/constants';
import { NavbarDropdownGroup, NavbarVariant } from 'modules/nav/types';
import { useRouteMatch } from 'react-router';
import { isFunction } from 'utils/helpers';
import { camelCase } from 'utils/stringHelpers';
import { LinkContainer } from '../LinkContainer';
import { Content, Item, Trigger } from './NavbarLinks.styled';

type NavbarLinkProps = {
  item: NavbarDropdownGroup;
  pathname: string;
  variant: NavbarVariant;
};

export const NavbarLink = ({ item, pathname, variant }: NavbarLinkProps) => {
  const { dropdownOptions = [] } = item;

  const urls = dropdownOptions?.flatMap(({ href, links = [] }) => {
    const childLinks = links.map((link) => link.href);
    return href ? [href as string, ...childLinks] : childLinks;
  });

  const hasMaxContent = dropdownOptions.some(
    ({ icon, pageDescription }) => !!pageDescription || !!icon,
  );

  const filteredUrls = urls.filter((url) => !isFunction(url) && !!url);
  const match = useRouteMatch(filteredUrls);

  /**
   * It is necessary to read pathname from useLocation rather than
   * match.path from useRouteMatch because match.path does not emit
   * the full path in some cases (/workflows instead of /workflows/webhooks).
   */
  const isItemActive = match?.isExact || filteredUrls.includes(pathname);

  const lowerCaseHeader = item.header.toLowerCase();

  return (
    <Item
      key={camelCase(item.header)}
      data-test={`${lowerCaseHeader}-item`}
      className={isItemActive ? ACTIVE_CLASS_NAME : ''}
    >
      <Trigger data-test={`${lowerCaseHeader}-trigger`} data-variant={variant}>
        {item.header}
      </Trigger>
      <Content
        data-test={`${lowerCaseHeader}-content`}
        data-content={hasMaxContent ? 'max' : 'min'}
      >
        <NavigationMenu.Link asChild>
          <>
            <LinkContainer
              dropdownOptions={item?.dropdownOptions}
              path={match?.path}
              shouldOpenInNewTab={!!item.shouldOpenInNewTab}
            />
          </>
        </NavigationMenu.Link>
      </Content>
    </Item>
  );
};
